


import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import DragDrop from "../../../components/drag-drop/DragDrop";
import { MuiLoadingButton, RoundedSquareIcon } from "../../../components";
import { Banner } from "../../../../core/models/db"; // Ajustar la ruta

interface BannerFormProps {
  loading?: boolean;
  initialBanner: Banner;
  onSubmit: (
    banner: Banner,
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    }
  ) => void;
}

const BannerForm: React.FC<BannerFormProps> = ({
  loading = false,
  initialBanner,
  onSubmit,
}) => {
  const [isEditing, setIsEditing] = useState(!initialBanner?.id);

  // Imagen
  const [image, setImage] = useState(initialBanner?.image || "");
  const [oldImage, setOldImage] = useState(initialBanner?.image || "");
  const [imageName, setImageName] = useState("");

  // Estado para manejar errores de imagen
  const [imageError, setImageError] = useState("");

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .max(100, "Máximo 100 caracteres.")
      .required("El título es obligatorio."),
    description: Yup.string().max(500, "Máximo 500 caracteres."),
    url: Yup.string()
      .transform((value) => {
        if (!value) return value;
        const trimmedValue = value.trim();
        if (
          !trimmedValue.startsWith("http://") &&
          !trimmedValue.startsWith("https://")
        ) {
          return `https://${trimmedValue}`;
        }
        return trimmedValue;
      })
      .url("URL inválida")
      .required("El enlace es obligatorio."),
    startPublicationDate: Yup.date()
      .nullable()
      .required("La fecha de inicio es obligatoria."),
    endPublicationDate: Yup.date()
      .nullable()
      .required("La fecha de fin es obligatoria.")
      .min(
        Yup.ref('startPublicationDate'),
        "La fecha de fin debe ser posterior a la fecha de inicio."
      ),
  });

  const formik = useFormik({
    initialValues: {
      id: initialBanner.id || undefined,
      title: initialBanner.title || "",
      description: initialBanner.description || "",
      url: initialBanner.url || "",
      isModal: initialBanner.isModal ?? false,
      isTopBanner: initialBanner.isTopBanner ?? false,
      startPublicationDate: initialBanner.startPublicationDate
        ? moment(initialBanner.startPublicationDate).toDate()
        : null,
      endPublicationDate: initialBanner.endPublicationDate
        ? moment(initialBanner.endPublicationDate).toDate()
        : null,
    },
    validationSchema,
    onSubmit: (values) => {
      // Resetear error de imagen
      setImageError("");

      // Validación adicional: si isModal es true, la imagen es obligatoria
      if (values.isModal && !image) {
        setImageError("La imagen es obligatoria cuando se muestra como Modal.");
        return;
      }

      const bannerToSave: Banner = {
        ...values,
        image,
        startPublicationDate: values.startPublicationDate
          ? moment(values.startPublicationDate).toDate()
          : null,
        endPublicationDate: values.endPublicationDate
          ? moment(values.endPublicationDate).toDate()
          : null,
      };

      onSubmit(bannerToSave, { oldImage, image, nameImage: values.title });
    },
    enableReinitialize: true,
  });

  // Manejo de imagen
  const handleFiles = (files: any[]) => {
    if (files.length === 0) {
      setImage("");
      formik.setFieldValue("image", "");
      // Si la imagen se elimina, verificar si isModal está activo para establecer error
      if (formik.values.isModal) {
        setImageError("La imagen es obligatoria cuando se muestra como Modal.");
      } else {
        setImageError("");
      }
    } else if (files?.[0].blob) {
      const file = files[0];
      const url = URL.createObjectURL(file.blob);
      setImage(url);
      formik.setFieldValue("image", url);
      // Limpiar error si la imagen se carga correctamente
      setImageError("");
    }
  };

  const changeToBackup = () => {
    setImage(null);
    setImageName("");
    setTimeout(() => {
      setImage(initialBanner?.image);
      setImageName(
        initialBanner?.image?.substring(initialBanner?.image?.lastIndexOf("/") + 1)
      );
      setOldImage(initialBanner?.image);
      // Al restaurar la imagen, limpiar cualquier error previo
      setImageError("");
    }, 200);
  };

  const handleCancelClick = () => {
    formik.resetForm();
    changeToBackup();
    setIsEditing(false);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Datos del Banner
        </Typography>
        <DragDrop
          id="banner-image"
          valueDefault={image}
          disabled={loading || !isEditing}
          directoryNameList={["banners", "images"]}
          maxSizeMB={2}
          typeAllowed={["image/*"]}
          isOnlyOneFile={true}
          isBlob={true}
          specialCase={true}
          showFileList={false}
          handleFiles={handleFiles}
        />
        {/* Mostrar error de imagen si existe */}
        {imageError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {imageError}
          </Typography>
        )}

        {/* Formulario */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label={
                <>
                  Título <Typography component="span" color="error">*</Typography>
                </>
              }
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.title && formik.errors.title)}
              helperText={formik.touched.title && typeof formik.errors.title === 'string' ? formik.errors.title : undefined}
              disabled={loading || (!isEditing && !!initialBanner.id)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label="Descripción"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.description && formik.errors.description)}
              helperText={formik.touched.description && typeof formik.errors.description === 'string' ? formik.errors.description : undefined}
              disabled={loading || (!isEditing && !!initialBanner.id)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="standard"
              label={
                <>
                  Enlace URL <Typography component="span" color="error">*</Typography>
                </>
              }
              name="url"
              value={formik.values.url.replace(/^https?:\/\//, "")}
              onChange={(e) => {
                const value = e.target.value.trim();
                formik.setFieldValue("url", value);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (!value.startsWith("http://") && !value.startsWith("https://")) {
                  formik.setFieldValue("url", `https://${value}`);
                }
                formik.handleBlur(e);
              }}
              error={Boolean(formik.touched.url && formik.errors.url)}
              helperText={
                formik.touched.url &&
                (typeof formik.errors.url === "string"
                  ? formik.errors.url
                  : "URL externa del banner (se añadirá automáticamente https:// si es necesario)")
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="textSecondary">
                      https://
                    </Typography>
                  </InputAdornment>
                ),
              }}
              disabled={loading || (!isEditing && !!initialBanner.id)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<RoundedSquareIcon checked={false} />}
                  checkedIcon={<RoundedSquareIcon checked={true} />}
                  disabled={loading || (!isEditing && !!initialBanner.id)}
                  name="isModal"
                  checked={formik.values.isModal}
                  onChange={(e) => {
                    formik.setFieldValue("isModal", e.target.checked);
                    // Si se desmarca isModal, limpiar cualquier error de imagen
                    if (!e.target.checked) {
                      setImageError("");
                    } else if (!image) {
                      setImageError("La imagen es obligatoria cuando se muestra como Modal.");
                    }
                  }}
                />
              }
              label="Mostrar como Modal"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Solo se puede tener un banner como modal al mismo tiempo.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<RoundedSquareIcon checked={false} />}
                  checkedIcon={<RoundedSquareIcon checked={true} />}
                  disabled={loading || (!isEditing && !!initialBanner.id)}
                  name="isTopBanner"
                  checked={formik.values.isTopBanner}
                  onChange={(e) => formik.setFieldValue("isTopBanner", e.target.checked)}
                />
              }
              label="Mostrar en el Top Menu"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Puede haber múltiples banners en el Top Menu.
            </Typography>
          </Grid>
        </Grid>
        
      </Box>

      <Box mt={4}>
        <Typography variant="h6">Fechas de Publicación</Typography>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={
                <>
                  Fecha de inicio <Typography component="span" color="error">*</Typography>
                </>
              }
              slotProps={{
                textField: {
                  variant: "standard",
                  fullWidth: true,
                  error: Boolean(formik.touched.startPublicationDate && formik.errors.startPublicationDate),
                  helperText: formik.touched.startPublicationDate && formik.errors.startPublicationDate ? String(formik.errors.startPublicationDate) : "",
                },
              }}
              value={
                formik.values.startPublicationDate
                  ? moment(formik.values.startPublicationDate)
                  : null
              }
              onChange={(v) =>
                formik.setFieldValue(
                  "startPublicationDate",
                  v ? v.toDate() : null
                )
              }
              disabled={loading || (!isEditing && !!initialBanner.id)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={
                <>
                  Fecha de fin <Typography component="span" color="error">*</Typography>
                </>
              }
              slotProps={{
                textField: {
                  variant: "standard",
                  fullWidth: true,
                  error: Boolean(formik.touched.endPublicationDate && formik.errors.endPublicationDate),
                  helperText: formik.touched.endPublicationDate && formik.errors.endPublicationDate ? String(formik.errors.endPublicationDate) : "",
                },
              }}
              value={
                formik.values.endPublicationDate
                  ? moment(formik.values.endPublicationDate)
                  : null
              }
              onChange={(v) =>
                formik.setFieldValue("endPublicationDate", v ? v.toDate() : null)
              }
              disabled={loading || (!isEditing && !!initialBanner.id)}
            />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mt: 1 }}>
          Si la fecha de fin está definida, el banner se ocultará automáticamente después de esa fecha.
        </Typography>

        {/* BOTONES DE ACCIÓN */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={5}>
          {!initialBanner?.id && (
            <Box>
              <MuiLoadingButton
                title="Crear"
                variant="contained"
                color="primary"
                type="submit"
                disabled={!formik.isValid || loading || (formik.values.isModal && !image)}
                isLoading={loading}
              />
            </Box>
          )}
          {initialBanner?.id && !isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsEditing(true)}
              disabled={loading}
            >
              Editar
            </Button>
          )}
          {initialBanner?.id && isEditing && (
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancelClick}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Box>
                <MuiLoadingButton
                  title="Guardar"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    !formik.isValid ||
                    loading ||
                    (formik.values.isModal && !image)
                  }
                  isLoading={loading}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </form>
  );
};

export default BannerForm;
